<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import ConfigCertificatesRepository from '../../repositories/configCertificates.repository';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPagination } from '../../types/pagination.type';
import { IConfigCertificate } from '../../types/configCertificate.type';
import { ICompany } from '../../types/company.type';
import { useToast } from '../../composables/useToast';
import { useI18n } from 'vue-i18n';
import CompaniesRepository from '@repositories/companies.repository';
import DataTable from '@components/DataTable.vue';

const isLoadingConfigCertificates = ref<boolean>(false);

const { t } = useI18n();
const store = Store();
const { $toast } = useToast();
const { configCertificates: configCertificatesPinia }: any = storeToRefs(store);
const configCertificates= ref<IConfigCertificate[]>([]);
const configToDeleteRestore= ref<IConfigCertificate | null>(null);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const showError = ref<string | null>(null);
const selectedConfigCertificatesRecord = ref<IConfigCertificate | null>(null);
const companies = ref<ICompany[]>([]);
const deletedValues = ref<boolean>(false);

const fields = computed(() => {
  return deletedValues.value
    ? [
        {
          key: 'id',
          label: t('global.id'),
          sortable: true,
        },
        {
          key: 'company',
          label: t('ajustes.configuracion_certificados.compania'),
          formatter: (e: any) => (e?.name ?? '-'),
        },
        {
          key: 'startDateMinTravel',
          label: t('ajustes.configuracion_certificados.dias_min'),
        },
        {
          key: 'maxValueAsset',
          label: t('ajustes.configuracion_certificados.val_max'),
        },
        
        {
          key: 'delete',
          label: t('global.restaurar'),
        },
      ]
    : [
      {
        key: 'id',
        label: t('global.id'),
        sortable: true,
      },
      {
        key: 'company',
        label: t('ajustes.configuracion_certificados.compania'),
        formatter: (e: any) => (e?.name ?? '-'),
      },
      {
        key: 'startDateMinTravel',
        label: t('ajustes.configuracion_certificados.dias_min'),
      },
      {
        key: 'maxValueAsset',
        label: t('ajustes.configuracion_certificados.val_max'),
      },
      {
        key: 'edit',
        label: t('global.editar'),
      },
      {
        key: 'delete',
        label: t('global.eliminar'),
      },
    ]
})

const getConfigCertificatesFormAPI = async (
  page: number,
  deleted: boolean = false,
) => {
  isLoadingConfigCertificates.value = true;
  try {
    const data = await ConfigCertificatesRepository.getConfigCerticates({
      page,
      deleted,
    });
    if (data?.certificatesConfig && Array.isArray(data.certificatesConfig)) {
      paginationData.value = data.pagination;
      return data.certificatesConfig;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching whiteListOfac:', error);
    return [];
  } finally {
    isLoadingConfigCertificates.value = false;
  }
};

const updateConfigCertificates = (newConfigCertificate: IConfigCertificate[]) => {
  configCertificates.value = newConfigCertificate;
  store.setProp({ key: 'configCertificates', value: newConfigCertificate });
};

const loadConfigCertificates = async () => {
  const newConfigCertificate = await getConfigCertificatesFormAPI(1);
  updateConfigCertificates(newConfigCertificate);
};

const fieldsInputConfigCertificateRecord = computed(() => {
  return [
    {
      key: 'companyId',
      title: 'Nombre del sponsor',
      type: 'select',
      label: 'name',
      placeholder: 'Buscar por sponsor',
      formatValue: (val: any) => val?.id ?? null,
      value: selectedConfigCertificatesRecord.value?.companyId,
      options: companies,
      cols: 12,
    },
    {
      key: 'startDateMinTravel',
      title: t('ajustes.configuracion_certificados.dias_min'),
      placeholder: t('ajustes.configuracion_certificados.dias_min'),
      type: 'number',
      value: selectedConfigCertificatesRecord.value?.startDateMinTravel,
      state: null,
      cols: 12,
      validations: {
        rules: {
          required: true,
        },
        messages: {
          required: t('global.es_obligatorio'),
        },
      },
    },
    {
      key: 'maxValueAsset',
      title: t('ajustes.configuracion_certificados.val_max'),
      placeholder: t('ajustes.configuracion_certificados.val_max'),
      type: 'number',
      value: selectedConfigCertificatesRecord.value?.maxValueAsset,
      state: null,
      cols: 12,
      validations: {
        rules: {
          required: true,
        },
        messages: {
          required: t('global.es_obligatorio'),
        },
      },
    },
  ];
});

const getCompaniesFromApi = async () => {
  const data = await CompaniesRepository.getCompanies();
  companies.value = data.companies;
};

const onChangePage = async (page: number) => {
  getConfigCertificatesFormAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = t('ajustes.configuracion_certificados.agregar');
  isModalOpen.value = true;
  modalType.value = 'modalCreateConfig';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  configToDeleteRestore.value = null;
  selectedConfigCertificatesRecord.value = null;
};

const handleCreateConfigCertificate = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;

  try {
    const data = await ConfigCertificatesRepository.createConfigCertificateRecord(
      items,
    );
    store.setProp({
      key: 'configCertificates',
      value: [...configCertificates.value, data],
    });
    $toast?.success(t('ajustes.configuracion_certificados.creada'));
  } catch (error) {
    $toast?.error(t('global.ocurrio_un_error'));
  }

  handleClose();
};

const handleSelectWhiteListOfacRecord = (
  configCertificatesRecordData: IConfigCertificate,
) => {
  modalTitle.value = t('ajustes.configuracion_certificados.actualizar');
  (isModalOpen.value = true), (modalType.value = 'modalUpdateConfig');
  selectedConfigCertificatesRecord.value = configCertificatesRecordData;
};

const handleUpdateConfigCertificate = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedConfigCertificatesRecord.value) return;

  const configCertificatesRecordForUpdate = {
    ...items,
    id: selectedConfigCertificatesRecord.value.id,
  };

  try {
    const data = await ConfigCertificatesRepository.updateConfigCertificateRecord(
      configCertificatesRecordForUpdate,
    );

    let index = store.configCertificates.findIndex(
      (value: any) => value.id === data.id,
    );

    if (index > -1) {
      store.setProp({ key: 'configCertificates', index, value: data });
    }
    $toast?.success(t('ajustes.configuracion_certificados.exito'));
  } catch (error) {
    $toast?.error(t('global.ocurrio_un_error'));
  }

  handleClose();
};

const handleModalDelete = (item: IConfigCertificate) => {
  modalTitle.value = !deletedValues.value
    ? `${ t('ajustes.configuracion_certificados.borrar_config_con_el_id')}: ${item.id}`
    : `${ t('ajustes.configuracion_certificados.restaurar_config_con_el_id')}: ${item.id}`;
  modalType.value = 'modalDelete';
  configToDeleteRestore.value = item;
  isModalOpen.value = true;
};

const handleActive = async () => {
  if (!deletedValues.value) return;
  const newConfigCertificate = await getConfigCertificatesFormAPI(1);
  updateConfigCertificates(newConfigCertificate);
  deletedValues.value = false;
};

const handleInactive = async () => {
  if (!!deletedValues.value) return;
  const newConfigCertificate = await getConfigCertificatesFormAPI(1, true);
  updateConfigCertificates(newConfigCertificate);
  deletedValues.value = true;
};

const handleDelete = async () => {
  if (configToDeleteRestore?.value?.id) {
    await ConfigCertificatesRepository.deleteConfigCertificate(configToDeleteRestore.value.id);

    const newConfigCertificate = await getConfigCertificatesFormAPI(paginationData.value?.current_page ?? 1, deletedValues.value);
    updateConfigCertificates(newConfigCertificate);
    $toast?.success(t('ajustes.configuracion_certificados.eliminado'));
    configToDeleteRestore.value = null;
    handleClose();
  }
};

const handleRestore = async () => {
  if (configToDeleteRestore?.value?.id) {
    await ConfigCertificatesRepository.restoreConfigCertificate(configToDeleteRestore.value.id);

    const newConfigCertificate = await getConfigCertificatesFormAPI(paginationData.value?.current_page ?? 1, deletedValues.value);
    updateConfigCertificates(newConfigCertificate);
    $toast?.success(t('ajustes.configuracion_certificados.restaurado'));
    configToDeleteRestore.value = null;
    handleClose();
  }
};

onMounted(async () => {
  await loadConfigCertificates();
  await getCompaniesFromApi();
});
</script>

<template>
  <Layout
    :title="t('ajustes.configuracion_certificados.configuracion_certificados')"
    :textColor="'color-secondary'"
  >
    <template #layout>
      <div class="d-flex justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>{{ t('global.activos') }}</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>{{ t('global.inactivos') }}</template>
          </CButton>
        </div>
        <div>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleOpenModalCreate"
          >
            <template #content>{{ t('ajustes.configuracion_certificados.agregar') }}</template>
          </CButton>
        </div>
      </div>

      <DataTable
        :loading="isLoadingConfigCertificates"
        :fields="fields"
        :items="configCertificatesPinia"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(edit)="configCertificatesRecordData">
          <div
            @click="
              handleSelectWhiteListOfacRecord(configCertificatesRecordData.data.item)
            "
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-slot:cell(delete)="configCertificatesRecordData">
          <CButton
            :background="!deletedValues ? 'bg-white' : 'bg-blue'"
            :customClass="`p-1`"
            type="button"
            @click="handleModalDelete(configCertificatesRecordData.data.item)"
          >
            <template v-slot:content>
              <img
                :src="
                  !deletedValues
                    ? '/icons/basura.svg'
                    : '/icons/restaurar.svg'
                "
                alt="eliminar"
              />
            </template>
          </CButton>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalDelete'">
          <h5 class="text-center">
            {{
              !deletedValues
                ? `${t('ajustes.configuracion_certificados.borrar_config_con_el_id')} ${configToDeleteRestore?.id}?`
                : `${t('ajustes.configuracion_certificados.restaurar_config_con_el_id')} ${configToDeleteRestore?.id}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleDelete"
              v-if="!deletedValues"
            >
              <template #content>
                <div>
                  <span>{{ t('global.eliminar') }}</span>
                </div>
              </template>
            </CButton>
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleRestore"
              v-else
            >
              <template #content>
                <div>
                  <span>{{ t('global.restaurar') }}</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
        <template v-if="modalType === 'modalCreateConfig'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputConfigCertificateRecord"
            @onSubmit="handleCreateConfigCertificate"
          >
            <template #buttons>
              <p v-if="showError" class="text-danger mt-3">{{ showError }}</p>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('ajustes.configuracion_certificados.agregar') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateConfig'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputConfigCertificateRecord"
            @onSubmit="handleUpdateConfigCertificate"
          >
            <template #buttons>
              <p v-if="showError" class="text-danger mt-3">{{ showError }}</p>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('global.actualizar') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
